import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import bookingChangesApi from '@/features/dashboard/bookingChanges/api/bookingChangesApi.ts';
import { bookingChangesApiQueryKeys } from '@/features/dashboard/bookingChanges/api/bookingChangesApi.queryKeys.ts';
import { bookingsApiQueryKeys } from '@/features/dashboard/bookings/api/bookingsApi.queryKeys.ts';
import { participantsApiQueryKeys } from '@/features/dashboard/participants/api/participantsApi.queryKeys.ts';
import { clientsApiQueryKeys } from '@/features/dashboard/clients/api/clientsApi.queryKeys.ts';
import { ExecuteWithdrawalRequest } from '@/features/dashboard/bookingChanges/types/dto/WithdrawalDto.ts';
import { invalidateTourStatsQueries } from '@/utils/invalidateTourStatsQueries.ts';

/**
 * Wprowadzenie rezygnacji — natychmiastowe i nieodwracalne.
 *
 * Backend zakłada paczkę `WITHDRAWAL` i od razu ją wprowadza, więc nie ma tu
 * kroku akceptacji ani szkicu do poprawienia. Odmówi, gdy rezerwacja ma otwartą
 * paczkę zmian.
 */
const useExecuteWithdrawal = (bookingId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: ExecuteWithdrawalRequest) =>
      bookingChangesApi.executeWithdrawal(bookingId, request),
    onSuccess: () => {
      // Rezygnacja przepisuje księgę, statusy uczestników i harmonogram rat —
      // ten sam zestaw kluczy co wprowadzenie paczki zmian.
      [
        bookingChangesApiQueryKeys.booking(bookingId),
        bookingsApiQueryKeys.byId(bookingId),
        participantsApiQueryKeys.all(),
        clientsApiQueryKeys.all(),
      ].forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
      invalidateTourStatsQueries(queryClient);
      toast.success('Rezygnacja została wprowadzona');
    },
    onError: (error: Error) => {
      toast.error('Nie udało się wprowadzić rezygnacji', {
        description: error.message,
      });
    },
  });
};

export default useExecuteWithdrawal;
